import { Component, DestroyRef, ElementRef, input, OnInit, viewChild, forwardRef, ChangeDetectionStrategy, inject } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ENTER, TAB } from '@angular/cdk/keycodes';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { AnchorType } from '../../../../../models';
import { CreateCampaignLogicService } from '../../../services/create-campaign-logic.service';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MakingWavesLoaderService } from 'src/app/modules/shared/services';
import { NestedTreeControl } from '@angular/cdk/tree';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { MatDialog } from '@angular/material/dialog';
import { GeneralDialogComponent } from '../../../../shared/components/general-dialog/general-dialog.component'
import { GeneralDialogModel } from '../../../../shared/components/general-dialog/general-dialog.model'
import { LabelsDialogContentDialog } from '../labels-dialog/labels-dialog.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

interface AnchorsNode {
  name: string;
  children?: AnchorsNode[];
}

@Component({
    selector: 'multi-input',
    templateUrl: './multi-input.component.html',
    styleUrls: ['./multi-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MultiInputComponent),
            multi: true,
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [DecimalPipe, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatChipsModule, MatIconModule, MatTooltipModule, MatProgressSpinnerModule]
})
export class MultiInputComponent implements OnInit, ControlValueAccessor {
  treeControl = new NestedTreeControl<AnchorsNode>(node => node.children);
  dataSource = new MatTreeNestedDataSource<AnchorsNode>();
  anchorType = input.required<AnchorType>();

  fileBrowse = viewChild<ElementRef>('fileBrowse');

  private destroyRef = inject(DestroyRef);
  private createCampaignService = inject(CreateCampaignLogicService);
  private loaderService = inject(MakingWavesLoaderService);
  public dialog = inject(MatDialog);

  addOnBlur = true;
  readonly separatorKeysCodes = [ENTER, TAB] as const;
  anchors: string[] = [];

  onChange: any = (value: any) => { };
  onTouched: any = () => { };
  collection: AnchorsNode[] = []

  writeValue(value: any): void {
    this.anchors = value;
  }

  hasChild = (_: number, node: AnchorsNode) => !!node.children && node.children.length > 0;

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    const regex = new RegExp(this.anchorType().regex);

    if (value && regex.test(value) && !this.anchors.includes(value)) {
      this.anchors.push(value);
      this.onChange(this.anchors);
      event.chipInput?.clear();
    }

    this.onTouched();
  }
  

  addAnchore(value: string): void {
    const regex = new RegExp(this.anchorType().regex);
    if (value && regex.test(value) && !this.anchors.includes(value)) {
      this.anchors.push(value);
      this.onChange(this.anchors);
    }
  }

  remove(anchor: string): void {
    const index = this.anchors.indexOf(anchor);
    if (index >= 0) {
      this.anchors.splice(index, 1);
      this.updateValuesFromFileToList(this.anchors);
    }
    this.onTouched();
  }

  async onChooseFile(event: any) {
    const inputElement = event.target as HTMLInputElement;
    const file = inputElement.files?.[0];
    if (file) {
      this.loaderService.showLoader();
      this.createCampaignService.getInputValuesFromFile(file)
        .catch((err) => {
          console.error(err);
          this.loaderService.hideLoader();
        }
        )
    }
    this.fileBrowse()!.nativeElement.value = "";
  }

  public updateValuesFromFileToList(values: string[]): Promise<void> {
    return new Promise((resolve) => {
      this.collection = [];
      this.dataSource.data = [];
      const regex = new RegExp(this.anchorType().regex);
      const index = 1000;

      for (let i = 0; i < values.length;) {
        let items = values.slice(i, i + index);
        const item: AnchorsNode = {
          name: i + '-' + ((i + index) > values.length ? values.length : (i + index))
        }
        item.children = [];
        for (let k = 0; k < items.length; k++) {
          const value = (items[k] || '').trim();
          if (value && regex.test(value)) {

            const child: AnchorsNode = { name: value }
            item.children?.push(child);
          }
        }
        if (item.children.length)
          this.collection.push(item);
        i += index;
      }
      this.dataSource.data = this.collection;
      const result = this.collection.flatMap(header => header.children?.flatMap(list => list.name));

      // this.anchorsCounter = result.length;
      this.onChange(result);
      resolve();
    });
  }

  public addValuesFromFileToList(values: string[]): Promise<void> {
    return new Promise((resolve) => {
      const regex = new RegExp(this.anchorType().regex);
      const index = 1000;

      for (let i = 0; i < values.length;) {
        let items = values.slice(i, i + index);
        const item: AnchorsNode = {
          name: i + '-' + ((i + index) > values.length ? values.length : (i + index))
        }
        item.children = [];
        for (let k = 0; k < items.length; k++) {
          const value = (items[k] || '').trim();
          if (value && regex.test(value)) {

            const child: AnchorsNode = { name: value }
            item.children?.push(child);
            this.addAnchore(value);
          }
        }
        if (item.children.length)
          this.collection.push(item);
        i += index;
      }
      this.dataSource.data = this.collection;
      const result = this.collection.flatMap(header => header.children?.flatMap(list => list.name));
      this.onChange(result);
      resolve();
    });
  }

  public clearAnchors() {
    this.collection = [];
    this.dataSource.data = [];
    this.anchors = [];
    this.onChange(this.anchors);
  }

  // This function checks for duplicate anchors and displays an alert if duplicate exists
  // It is relevant only for loading anchors from file
  private alertDuplicateAnchors(anchors: string[]) {
    const duplicates = [...new Set(
      anchors.filter((item, index) =>
        anchors.indexOf(item) !== index // item appears again later
      )
    )];

    if (duplicates?.length) {
      let dialogContent = `<p>נמצאו ${duplicates.length} עוגנים כפולים:</p>`
      dialogContent += "<div style='overflow-y: auto; max-height: 145px;'>"
      for (const item of duplicates) {
        dialogContent += `<p>${item}</p>`
      }
      dialogContent += "</div>"
      dialogContent += "<p style='font-weight: bold; padding-top: 35px;'>לחיצה על אישור תטען ללא כפולים</p>"
      this.showDialog('לתשומת ליבך', dialogContent, anchors)
    }
    else {
      this.addValuesFromFileToList(anchors);
    }
  }

  private showDialog(title: string, dialogContent: string, anchors: string[]): void {
    const dialogData: GeneralDialogModel = {
      dialogTitle: title,
      dialogContent: dialogContent,
      isOkayButton: true,
      isCancelButton: true
    };

    const dialogRef = this.dialog.open(GeneralDialogComponent, { width: '512px', data: dialogData });

    dialogRef.afterClosed().subscribe(result => {
      if (result == true) {
        // remove duplicates
        const uniqueAnchors = [...new Set(anchors)];
        this.addValuesFromFileToList(uniqueAnchors);
      }
      else {
        // do not load anchors to list
      }
    });
  }

  public openDialog(): void {
    const dialogRef = this.dialog.open(LabelsDialogContentDialog, {
      width: '700px',   // or '80vw' for 80% of viewport
      disableClose: true, 
    });

    dialogRef.afterClosed().subscribe(results => {
      this.anchors = [...new Set([...this.anchors, ...results.groups])];     
      this.onChange(this.anchors);
    });
  }

  ngOnInit(): void {
    this.createCampaignService.getStringArrayValues$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: values => {
          values = values.map(item => item.trim().toLowerCase());
          this.loaderService.hideLoader();
          this.alertDuplicateAnchors(values);
          this.onTouched();
        },
        error: err => console.error('Observable emitted an error: ' + err),
        complete: () => { this.loaderService.hideLoader(); }
      });
  }

}
